import DebtorTable from "@/components/back-office/customers/debtors/debtor-table";
import UserTable from "@/components/back-office/customers/user-management/user-table";
import OrderTable from "@/components/debtor/orders/order-table";
import { filter_type, read_limit_cookie_server_side } from "@/components/shared/cookie-jar";
import DialogActionCancelSubmit from "@/components/shared/dialog/dialog-action-cancel-submit";
import DialogWrapper from "@/components/shared/dialog/dialog-wrapper";
import { clientFetch, url } from "@/components/shared/fetch-client/fetch_client";
import ReadSessionWrapper from "@/components/shared/read-session-wrapper";
import { homepage_tour_steps } from "@/components/tours/homepage-tour";
import tourObject from "@/components/tours/tour-object";
import { delete_tour_parameter_from_url } from "@/components/tours/tour-utils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BackupIcon from "@mui/icons-material/Backup";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import StorageIcon from "@mui/icons-material/Storage";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { getSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardWrapper from "../components/shared/dashboard_wrapper";
import Page from "../components/shared/page";
import { fetch_middleware } from "./api/middleware";

export default function Home(props) {
	const { enable_tour } = props;
	const [qemuList] = useState(props.qemu_list);
	const [domainList] = useState(props.domain_list);
	const [pbsList] = useState(props.pbs_list);
	const [dadupList] = useState(props.dadup_list);
	const [user] = useState(props.user);
	const { t } = useTranslation();

	return (
		<Page title="Home">
			<DashboardWrapper>
				<ReadSessionWrapper>
					{({ debtor, user_permissions, user_details }) => (
						<>
							<Grid driverjs-element="homepage" container spacing={2}>
								<Grid item xs={12}>
									<Typography variant="h5">
										{t("home.welcome")}{" "}
										<strong>
											{user_details.given_name} {user_details.middle_name}{" "}
											{user_details.family_name}
										</strong>
										!
									</Typography>
								</Grid>
								{user.superuser ? (
									/* ------------------------------- BACK-OFFICE ------------------------------ */
									<SuperUserSection />
								) : (
									/* ------------------------------- CUSTOMERS ------------------------------ */
									<>
										<ProductCardSection
											active={!debtor.paid_services_disabled}
											count={qemuList.count}
											title={t("home.overview_cards.vps.title")}
											icon={<StorageIcon sx={{ color: "rgb(0, 0, 0, .3)", fontSize: 60 }} />}
											button_text={t("home.overview_cards.vps.go_to")}
											button_link="/vps"
											color_schema="success"
										/>
										<ProductCardSection
											active={!debtor.paid_services_disabled}
											count={domainList.count}
											title={t("home.overview_cards.domain.title")}
											icon={<BookmarksIcon sx={{ color: "rgb(0, 0, 0, .3)", fontSize: 60 }} />}
											button_text={t("home.overview_cards.domain.go_to")}
											button_link="/domain"
											color_schema="company_light"
										/>
										<ProductCardSection
											active={true}
											count={pbsList.count}
											title={t("home.overview_cards.pbs.title")}
											icon={<CloudSyncIcon sx={{ color: "rgb(0, 0, 0, .3)", fontSize: 60 }} />}
											button_text={t("home.overview_cards.pbs.go_to")}
											button_link="/pbs"
											color_schema="proxmox_colors"
										/>
										<ProductCardSection
											active={!debtor.paid_services_disabled}
											count={dadupList.count}
											title={t("home.overview_cards.dadup.title")}
											icon={<BackupIcon sx={{ color: "rgb(0, 0, 0, .3)", fontSize: 60 }} />}
											button_text={t("home.overview_cards.dadup.go_to")}
											button_link="/dadup"
											color_schema="info"
										/>
									</>
								)}
							</Grid>
							{/* ------------------------------- TOUR DIALOG ------------------------------ */}
							<TourDialog
								debtor={debtor}
								user_permissions={user_permissions}
								enable_tour={enable_tour}
							/>
						</>
					)}
				</ReadSessionWrapper>
			</DashboardWrapper>
		</Page>
	);
}

/* -------------------------------------------------------------------------- */
/*                            PRODUCT CARD SECTION                            */
/* -------------------------------------------------------------------------- */
const ProductCardSection = ({
	count = 0,
	title,
	icon,
	button_text,
	button_link,
	color_schema = "secondary",
	active = false,
}) => {
	/* ---------------------------------- HOOKS --------------------------------- */
	const [loading, setLoading] = useState(false);
	const router = useRouter();

	// Return empty not active
	if (!active) {
		return null;
	}

	return (
		<Grid item xs={12} md={6} lg={3} xxl={2}>
			<Box
				sx={{
					":hover": {
						backgroundColor: `${color_schema}.dark`,
					},
					backgroundColor: `${color_schema}.main`,
					borderRadius: 4,
					cursor: "pointer",
					transition: "background-color .1s linear",
				}}
				onClick={() => {
					setLoading(true);
					router.push(button_link);
				}}
			>
				<Grid container>
					<Grid
						item
						xs={8}
						sx={{ pl: 3, py: 2 }}
						justifyContent="left"
						alignItems="center"
						display="flex"
					>
						<Grid container sx={{ color: "white" }}>
							<Grid item xs={12}>
								<Typography variant="h3">{count}</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography variant="h6">{title}</Typography>
							</Grid>
						</Grid>
					</Grid>
					<Grid
						item
						xs={4}
						justifyContent="right"
						alignItems="center"
						display="flex"
						sx={{ pr: 2 }}
					>
						{icon}
					</Grid>
					<Grid
						item
						xs={12}
						sx={{
							backgroundColor: "rgb(0, 0, 0, .3)",
							borderBottomLeftRadius: 16,
							borderBottomRightRadius: 16,
						}}
						color="#fff"
					>
						<LoadingButton
							display="flex"
							disableElevation
							disableRipple
							disableFocusRipple
							disableTouchRipple
							fullWidth
							loading={loading}
							loadingPosition="end"
							loadingIndicator={<CircularProgress size={16} sx={{ color: "white" }} />}
							sx={{ px: 2, py: 0.5 }}
							endIcon={<ArrowForwardIcon sx={{ color: "white", fontSize: 15 }} />}
						>
							<Typography sx={{ color: "white" }} variant="caption">
								{button_text}
							</Typography>
						</LoadingButton>
					</Grid>
				</Grid>
			</Box>
		</Grid>
	);
};

function SuperUserSection() {
	const { t } = useTranslation();

	return (
		<>
			<Grid item sx={{ mb: -4 }}>
				<Typography variant="h5">{t("company.tabs.orders")}</Typography>
			</Grid>
			<Grid item xs={12}>
				<OrderTable is_back_office url_params_enabled={false} />
			</Grid>
			<Grid item sx={{ mb: -4 }}>
				<Typography variant="h5">{t("back_office.customers.debtors.title")}</Typography>
			</Grid>
			<Grid item xs={12}>
				<DebtorTable url_params_enabled={false} />
			</Grid>
			<Grid item sx={{ mb: -4 }}>
				<Typography variant="h5">{t("back_office.customers.user_management.title")}</Typography>
			</Grid>
			<Grid item xs={12}>
				<UserTable url_params_enabled={false} />
			</Grid>
		</>
	);
}

function TourDialog({ debtor, user_permissions, enable_tour }) {
	// Hooks
	const [dialogOpen, setDialogOpen] = useState(enable_tour);

	const router = useRouter();
	const { t } = useTranslation();

	// Handlers
	const onCloseHandler = () => {
		// Close dialog
		setDialogOpen(false);

		// Delete tour parameter from url
		delete_tour_parameter_from_url(router);

		// Update database
		updateTutorialSeenFlag();
	};

	const onSubmitHandler = () => {
		// Start tour
		tourObject.setSteps(homepage_tour_steps(t, debtor, user_permissions));
		tourObject.drive();

		// Close dialog
		setDialogOpen(false);

		// Delete tour parameter from url
		delete_tour_parameter_from_url(router);

		// Update database
		updateTutorialSeenFlag();
	};

	const updateTutorialSeenFlag = async () => {
		// Update the flag in the database
		await clientFetch(url`/api/debtor/user/homepage-tutorial`).patch();
	};

	return (
		<DialogWrapper
			title={t("driver_tour.homepage_tour.steps.firstview.title")}
			open={dialogOpen}
			on_close={() => {}}
			disable_escape
		>
			{t("driver_tour.homepage_tour.steps.firstview.description")}

			<DialogActionCancelSubmit
				on_close={onCloseHandler}
				on_close_text={t("buttons.cancel")}
				on_submit_text={t("buttons.start_tour")}
				on_submit={onSubmitHandler}
			/>
		</DialogWrapper>
	);
}

/**
 * @type {import('next').GetServerSideProps}
 */
export async function getServerSideProps(context) {
	const { req, query, resolvedUrl } = context;
	const session = await getSession(context);

	if (session?.portal?.user?.seen_homepage_tutorial === false && !query?.tour) {
		// Fetch user-details
		const response = await fetch_middleware(req, "/debtor/user");
		const body = await response.json();

		// We check the API for the status, because it's not properly updated in the session yet.
		// This API call is only done when the 'session' still thinks the 'seen_homepage_tutorial' is still false.
		if (!body?.seen_homepage_tutorial) {
			return {
				redirect: {
					destination: `${resolvedUrl}?tour=true`,
					permanent: false,
				},
			};
		}
	}

	// Variables
	let response;
	const props = {};

	// Fetch user
	response = await fetch_middleware(req, "/debtor/user");
	const user = await response.json();
	props.user = user;
	props.enable_tour = Boolean(query?.tour);

	/* ------------------------------- BACKOFFICE ------------------------------- */
	if (user.superuser) {
		const limitDefault = 5;

		// Fetch order list
		const orderLimit = read_limit_cookie_server_side(req, filter_type.ORDER_LIMIT, limitDefault);
		response = await fetch_middleware(req, `/back-office/order?limit=${orderLimit}`);
		props.order_list = await response.json();
	}

	/* -------------------------------- CUSTOMERS ------------------------------- */
	if (!user.superuser) {
		// Fetch qemu
		response = await fetch_middleware(req, "/vps/qemu");
		props.qemu_list = await response.json();

		// Fetch domain list
		response = await fetch_middleware(req, "/domain/debtor?limit=-1&offset=0");
		props.domain_list = await response.json();

		// Fetch PBS list
		response = await fetch_middleware(req, "/pbs/");
		props.pbs_list = await response.json();

		//
		response = await fetch_middleware(req, "/dadup/");
		props.dadup_list = await response.json();
	}

	return { props: props };
}
